import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatDate, formatMonth } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Upload, Download, Trash2 } from "lucide-react";

function BulkImportDialog({ open, setOpen, endpoint, kindLabel, hasMonth, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(`${endpoint}/import/preview`, fd); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`${endpoint}/import/commit`, { rows: preview.rows }); toast.success(`Imported ${data.inserted} ${kindLabel.toLowerCase()}s`); setOpen(false); setPreview(null); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) setPreview(null); }}>
    <DialogContent className="max-w-4xl"><DialogHeader><DialogTitle>Bulk {kindLabel} Import — Preview</DialogTitle></DialogHeader>
      {!preview ? <div><Input type="file" accept=".xlsx,.xls" onChange={e => e.target.files[0] && upload(e.target.files[0])} data-testid={`${kindLabel.toLowerCase()}-import-file`} />
        <p className="text-xs text-muted-foreground mt-2">Dates: DD-MM-YYYY. Amount in ₹ (numeric). {hasMonth && "Payroll month optional as MM-YYYY."}</p></div>
        : <div><div className="text-sm mb-3">Valid rows: <b className="text-green-500">{preview.valid_count}</b> · Errors: <b className="text-red-400">{preview.error_count}</b></div>
          <div className="max-h-96 overflow-auto border rounded"><table className="plutus"><thead><tr><th>Row</th><th>Employee</th><th>Date</th><th className="text-right">Amount</th>{hasMonth && <th>Payroll</th>}<th>Errors</th></tr></thead>
            <tbody>{preview.rows.map(r => <tr key={r.row} className={r.valid ? "" : "bg-red-500/5"}>
              <td>{r.row}</td><td><span className="font-mono text-xs mr-2">{r.employee_id_input}</span>{r.employee_name}</td>
              <td className="font-mono text-xs">{r.date}</td><td className="text-right tabular-nums">{r.amount ? formatINR(r.amount) : ""}</td>
              {hasMonth && <td className="font-mono text-xs">{r.payroll_month || "-"}</td>}
              <td className="text-red-400 text-xs">{r.errors.join(", ")}</td></tr>)}</tbody></table></div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setPreview(null)}>Choose another file</Button>
            <Button className="gold-bg" disabled={busy || preview.valid_count === 0} onClick={commit} data-testid={`${kindLabel.toLowerCase()}-import-commit`}>Commit {preview.valid_count} rows</Button></DialogFooter></div>}
    </DialogContent></Dialog>;
}

export function makeTxnPage({ title, endpoint, hasMonth, kindLabel, supportsImport }) {
  return function Page() {
    const [list, setList] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [importOpen, setImportOpen] = useState(false);

    const load = async () => { const { data } = await api.get(endpoint); setList(data); };
    useEffect(() => { load(); api.get("/employees").then(({ data }) => setEmployees(data)); }, []);

    const openNew = () => setEditing({ employee_id: employees[0]?.id || "", date: new Date().toISOString().slice(0, 10), amount: "", payroll_month: "" });
    const doSave = async () => {
      try {
        const body = { ...editing };
        if (!hasMonth || !body.payroll_month) delete body.payroll_month;
        if (editing.id) await api.put(`${endpoint}/${editing.id}`, body);
        else await api.post(endpoint, body);
        toast.success("Saved"); setOpen(false); load();
      } catch (e) { toast.error(apiError(e)); }
    };
    const doDelete = async (row) => {
      try { await api.delete(`${endpoint}/${row.id}`); toast.success(`${kindLabel} deleted`); load(); }
      catch (e) { toast.error(apiError(e)); }
    };
    const downloadTemplate = async () => {
      const res = await api.get(`${endpoint}/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${kindLabel.toLowerCase()}_template.xlsx`; a.click();
    };

    return <div className="space-y-6" data-testid={`${title.toLowerCase().replace(/ /g,'-')}-page`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div><div className="overline">Ledger</div><h1 className="font-serif text-4xl mt-1">{title}</h1></div>
        <div className="flex gap-2">
          {supportsImport && <>
            <Button variant="outline" onClick={downloadTemplate} data-testid={`${kindLabel.toLowerCase()}-template-btn`}><Download className="w-4 h-4 mr-2" />Template</Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`${kindLabel.toLowerCase()}-import-btn`}><Upload className="w-4 h-4 mr-2" />Import Excel</Button>
          </>}
          <Button className="gold-bg" onClick={() => { openNew(); setOpen(true); }} data-testid={`new-${kindLabel.toLowerCase()}-btn`}><Plus className="w-4 h-4 mr-2" />Record {kindLabel}</Button>
        </div>
      </div>
      <div className="border rounded-md overflow-x-auto"><table className="plutus"><thead><tr>
        <th>Date</th><th>Employee</th>{hasMonth && <th>Payroll Month</th>}<th className="text-right">Amount</th><th></th>
      </tr></thead><tbody data-testid={`${title.toLowerCase().replace(/ /g,'-')}-list`}>
        {list.map(x => <tr key={x.id}>
          <td className="font-mono text-xs">{formatDate(x.date)}</td>
          <td><span className="font-mono text-xs text-muted-foreground mr-2">{x.employee_code}</span>{x.employee_name}</td>
          {hasMonth && <td>{x.payroll_month ? <span className="pill pill-outstanding">{formatMonth(x.payroll_month)}</span> : <span className="text-muted-foreground text-xs">Additional</span>}</td>}
          <td className="text-right tabular-nums gold">{formatINR(x.amount)}</td>
          <td className="text-right whitespace-nowrap">
            <button className="text-sm text-muted-foreground hover:text-foreground mr-3" onClick={() => { setEditing({ ...x, employee_id: x.employee_id }); setOpen(true); }} data-testid={`edit-${kindLabel.toLowerCase()}-${x.id}`}>Edit</button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="text-sm text-red-400 hover:text-red-300" data-testid={`delete-${kindLabel.toLowerCase()}-${x.id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete {kindLabel.toLowerCase()}?</AlertDialogTitle>
                  <AlertDialogDescription>Permanently delete this {kindLabel.toLowerCase()} of <b className="gold">{formatINR(x.amount)}</b> dated {formatDate(x.date)}? All balances will be recalculated. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => doDelete(x)} className="bg-red-600 hover:bg-red-500 text-white" data-testid={`confirm-delete-${kindLabel.toLowerCase()}-${x.id}`}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </td>
        </tr>)}
        {list.length === 0 && <tr><td colSpan={hasMonth ? 5 : 4} className="text-center py-10 text-muted-foreground">No records yet.</td></tr>}
      </tbody></table></div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editing?.id ? `Edit ${kindLabel}` : `Record ${kindLabel}`}</DialogTitle></DialogHeader>
          {editing && <div className="space-y-4">
            <div><Label>Employee *</Label><Select value={editing.employee_id} onValueChange={v => setEditing({ ...editing, employee_id: v })}><SelectTrigger data-testid="txn-emp-select"><SelectValue /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.employee_id} — {e.name} · Out: {formatINR(e.outstanding)}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={editing.date} onChange={e => setEditing({ ...editing, date: e.target.value })} data-testid="txn-date" /></div>
              <div><Label>Amount (₹) *</Label><Input type="number" value={editing.amount} onChange={e => setEditing({ ...editing, amount: e.target.value })} data-testid="txn-amount" /></div>
            </div>
            {hasMonth && <div><Label>Payroll Month <span className="text-xs text-muted-foreground">(leave blank for Additional Repayment)</span></Label>
              <Input type="month" value={editing.payroll_month || ""} onChange={e => setEditing({ ...editing, payroll_month: e.target.value })} data-testid="txn-payroll-month" /></div>}
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <AlertDialog><AlertDialogTrigger asChild><Button className="gold-bg" data-testid="save-txn-btn">Save</Button></AlertDialogTrigger>
              <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Confirm {kindLabel}</AlertDialogTitle>
                <AlertDialogDescription>Save {kindLabel.toLowerCase()} of <b className="gold">{formatINR(editing?.amount || 0)}</b> on {editing?.date}?</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doSave} data-testid="confirm-save-txn">Confirm</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {supportsImport && <BulkImportDialog open={importOpen} setOpen={setImportOpen} endpoint={endpoint} kindLabel={kindLabel} hasMonth={hasMonth} onDone={load} />}
    </div>;
  };
}

export const Advances = makeTxnPage({ title: "Advances", endpoint: "/advances", hasMonth: false, kindLabel: "Advance", supportsImport: true });
export default Advances;
